import type { Theme } from "@/data/photographers";

export type MagazineSection = {
  heading: string;
  body: string;
};

export type MagazineFaq = {
  q: string;
  a: string;
};

export type MagazineTopic = {
  /** URL slug, used in /magazin/$topic */
  slug: string;
  /** English URL slug, also resolvable in /magazin/$topic */
  slugEn: string;
  /** Matching gallery theme id, used to pull works and photographers */
  theme: Theme;
  /** Editorial display label */
  label: string;
  /** English editorial display label */
  labelEn: string;
  /** <title> + H1 — keep keyword-forward and < 60 chars where possible */
  title: string;
  /** English <title> + SEO title */
  titleEn: string;
  /** Meta description, < 160 chars */
  metaDescription: string;
  /** English meta description, < 160 chars */
  metaDescriptionEn: string;
  /** Short eyebrow above the H1 */
  eyebrow: string;
  /** English eyebrow */
  eyebrowEn: string;
  /** Hero standfirst / lede */
  lede: string;
  /** English hero lede */
  ledeEn: string;
  /** Editorial intro paragraph(s) */
  intro: string;
  /** English intro */
  introEn: string;
  /** Magazine-style content sections */
  sections: MagazineSection[];
  /** English sections */
  sectionsEn: MagazineSection[];
  /** Pull quote */
  pullQuote: string;
  /** English pull quote */
  pullQuoteEn: string;
  /** Checklist of what to look for / how to prepare */
  checklist: string[];
  /** English checklist */
  checklistEn: string[];
  /** FAQ — also rendered as FAQPage structured data */
  faq: MagazineFaq[];
  /** English FAQ */
  faqEn: MagazineFaq[];
};

export const magazineTopics: MagazineTopic[] = [
  {
    slug: "portraitfotografie",
    slugEn: "portrait-photography",
    theme: "portraits",
    label: "Portraitfotografie",
    labelEn: "Portrait Photography",
    title: "Portraitfotografie: Editorial-Porträts buchen",
    metaDescription:
      "Portraitfotografie auf Magazin-Niveau: Was gute Porträts ausmacht, wie ein Shooting abläuft und wie du die passende Fotografin oder den passenden Fotografen findest.",
    titleEn: "Portrait Photography: Book Curated Photographers",
    metaDescriptionEn:
      "Find curated portrait photographers on OM Selected: what makes a great portrait, how a shoot works and how to book the right photographers and visagists.",
    eyebrow: "Magazin, Portrait",
    lede: "Ein gutes Porträt zeigt nicht, wie jemand aussieht, sondern wie jemand ist.",
    intro:
      "Portraitfotografie lebt von Vertrauen, Licht und Timing. Es geht nicht um perfekte Posen, sondern um den Moment, in dem die Person vor der Kamera für einen Augenblick vergisst, dass sie fotografiert wird. Genau dort entsteht Charakter, eine ruhige Stille, die ein Bild über Jahre hinweg lesbar macht.",
    sections: [
      {
        heading: "Was ein starkes Porträt ausmacht",
        body: "Komposition, Lichtführung und Nähe entscheiden über die Wirkung. Weiches Fensterlicht modelliert Gesichtszüge sanft, hartes Licht setzt Kanten und Charakter. Entscheidend ist die Verbindung zwischen Fotograf:in und Person: Wer sich wohlfühlt, schaut anders in die Kamera.",
      },
      {
        heading: "So läuft ein Portrait-Shooting ab",
        body: "Vor dem Termin werden Stimmung, Looks und Locations besprochen. Während des Shootings wechseln sich konzentrierte und lockere Phasen ab. Statt hundert gestellter Bilder zählen die wenigen ehrlichen Momente, aus denen am Ende die finale Auswahl entsteht.",
      },
      {
        heading: "Editorial, Business oder persönlich",
        body: "Ob redaktionelle Strecke, Headshot fürs Unternehmen oder ein persönliches Porträt für die Familie, die Bildsprache passt sich dem Anlass an. Auf OM Selected findest du kuratierte Fotograf:innen, deren Handschrift zu deinem Vorhaben passt.",
      },
    ],
    pullQuote: "Das beste Porträt entsteht, wenn die Technik unsichtbar wird.",
    checklist: [
      "Anlass und Verwendung klären (Web, Print, Social)",
      "Outfits in ruhigen Farben und Materialien wählen",
      "Referenzbilder als Stimmung mitbringen",
      "Genug Zeit einplanen, gute Porträts brauchen keine Eile",
    ],
    faq: [
      {
        q: "Was kostet ein professionelles Portrait-Shooting?",
        a: "Die Preise variieren je nach Erfahrung, Umfang und Region. Über das Briefing auf OM Selected erhältst du passende Angebote von kuratierten Fotograf:innen.",
      },
      {
        q: "Wie lange dauert ein Portrait-Shooting?",
        a: "Ein fokussiertes Porträt-Shooting dauert meist ein bis zwei Stunden, inklusive Vorgespräch und mehreren Looks.",
      },
      {
        q: "Wie viele bearbeitete Bilder bekomme ich?",
        a: "Das hängt vom Paket ab. Üblich ist eine kuratierte Auswahl der stärksten Aufnahmen, professionell retuschiert.",
      },
    ],
    eyebrowEn: "Magazine, Portrait",
    ledeEn: "A good portrait doesn't show what someone looks like, but who they are.",
    introEn:
      "Portrait photography thrives on trust, light and timing. It's not about perfect poses, but about the moment when the person in front of the camera briefly forgets they're being photographed. That's exactly where character appears, a quiet stillness that keeps an image readable for years.",
    sectionsEn: [
      {
        heading: "What makes a strong portrait",
        body: "Composition, lighting and closeness determine the impact. Soft window light models facial features gently, hard light sets edges and character. What matters most is the connection between photographer and subject: someone who feels at ease looks into the camera differently.",
      },
      {
        heading: "How a portrait shoot works",
        body: "Before the session, mood, looks and locations are discussed. During the shoot, focused and relaxed phases alternate. Instead of a hundred staged shots, it's the few honest moments that count and from which the final selection emerges.",
      },
      {
        heading: "Editorial, business or personal",
        body: "Whether an editorial spread, a corporate headshot or a personal portrait for the family, the visual language adapts to the occasion. On OM Selected you'll find curated photographers whose signature suits your project.",
      },
    ],
    pullQuoteEn: "The best portrait happens when the technique becomes invisible.",
    checklistEn: [
      "Clarify the occasion and use (web, print, social)",
      "Choose outfits in calm colours and materials",
      "Bring reference images for mood",
      "Plan enough time, good portraits aren't rushed",
    ],
    faqEn: [
      {
        q: "What does a professional portrait shoot cost?",
        a: "Prices vary by experience, scope and region. Through the briefing on OM Selected you'll receive matching offers from curated photographers.",
      },
      {
        q: "How long does a portrait shoot take?",
        a: "A focused portrait shoot usually lasts one to two hours, including a pre-talk and several looks.",
      },
      {
        q: "How many edited images do I get?",
        a: "That depends on the package. Typically a curated selection of the strongest shots, professionally retouched.",
      },
    ],
  },
  {
    slug: "hochzeitsfotografie",
    slugEn: "wedding-photography",
    theme: "wedding",
    label: "Hochzeitsfotografie",
    labelEn: "Wedding Photography",
    title: "Hochzeitsfotografie: Fotograf:innen für eure Hochzeit",
    titleEn: "Wedding Photographers & Visagists, OM Selected",
    metaDescription:
      "Dokumentarische Hochzeitsfotografie mit Haltung: ehrliche Momente statt steifer Posen. So findet ihr die passende Hochzeitsfotografin oder den passenden Fotografen.",
    metaDescriptionEn:
      "Find curated wedding photographers and visagists on OM Selected: documentary photography with attitude, honest moments instead of stiff poses.",
    eyebrow: "Magazin, Hochzeit",
    lede: "Die schönsten Hochzeitsbilder sind die, an die sich niemand erinnert, dass sie gemacht wurden.",
    intro:
      "Dokumentarische Hochzeitsfotografie erzählt euren Tag so, wie er wirklich war, mit echten Blicken, leisen Tränen und dem Lachen zwischen den großen Momenten. Statt langer Posier-Sessions arbeiten die besten Fotograf:innen unauffällig und nah dran.",
    sections: [
      {
        heading: "Reportage statt Inszenierung",
        body: "Eine gute Hochzeitsreportage begleitet den Tag wie eine Erzählung: vom Getting Ready über die Zeremonie bis zur Feier. Der dokumentarische Blick fängt Stimmungen ein, ohne den Ablauf zu unterbrechen.",
      },
      {
        heading: "Den richtigen Stil finden",
        body: "Hell und zeitlos, warm und filmisch oder kontrastreich und editorial, jede Fotograf:in hat eine eigene Handschrift. Schaut euch ganze Hochzeiten an, nicht nur einzelne Highlights, um zu sehen, wie konstant die Qualität ist.",
      },
      {
        heading: "Frühzeitig anfragen",
        body: "Gefragte Hochzeitsfotograf:innen sind oft ein Jahr im Voraus ausgebucht. Über OM Selected könnt ihr euer Datum und eure Region angeben und passende, kuratierte Profile vergleichen.",
      },
    ],
    pullQuote: "Eine Hochzeit dauert einen Tag, die Bilder bleiben ein Leben lang.",
    checklist: [
      "Datum, Region und ungefähren Ablauf festhalten",
      "Ganze Hochzeitsgalerien statt nur Highlights ansehen",
      "Frühzeitig anfragen, beliebte Termine sind schnell vergeben",
      "Ein Kennenlernen vereinbaren, Vertrauen ist entscheidend",
    ],
    faq: [
      {
        q: "Wann sollten wir die Hochzeitsfotografin buchen?",
        a: "Idealerweise sechs bis zwölf Monate vorher, beliebte Termine in der Hochsaison sind oft früh ausgebucht.",
      },
      {
        q: "Wie lange begleitet uns die Fotograf:in?",
        a: "Von wenigen Stunden bis zur Ganztagsbegleitung ist alles möglich. Das richtet sich nach eurem Ablauf und Budget.",
      },
      {
        q: "Wann bekommen wir die Bilder?",
        a: "Eine sorgfältige Auswahl und Bearbeitung dauert in der Regel einige Wochen. Eine kleine Vorschau gibt es oft schon vorab.",
      },
    ],
    eyebrowEn: "Magazine, Wedding",
    ledeEn: "The most beautiful wedding photos are the ones no one remembers being taken.",
    introEn:
      "Documentary wedding photography tells your day the way it really was, with genuine glances, quiet tears and the laughter between the big moments. Instead of long posing sessions, the best photographers work discreetly and up close.",
    sectionsEn: [
      {
        heading: "Reportage instead of staging",
        body: "A good wedding reportage accompanies the day like a narrative: from getting ready through the ceremony to the celebration. The documentary eye captures moods without interrupting the flow.",
      },
      {
        heading: "Finding the right style",
        body: "Bright and timeless, warm and cinematic, or high-contrast and editorial, every photographer has their own signature. Look at entire weddings, not just single highlights, to see how consistent the quality is.",
      },
      {
        heading: "Enquire early",
        body: "In-demand wedding photographers are often booked a year in advance. With OM Selected you can specify your date and region and compare matching, curated profiles.",
      },
    ],
    pullQuoteEn: "A wedding lasts a day, the images last a lifetime.",
    checklistEn: [
      "Note the date, region and rough schedule",
      "View entire wedding galleries, not just highlights",
      "Enquire early, popular dates go fast",
      "Arrange a first meeting, trust is essential",
    ],
    faqEn: [
      {
        q: "When should we book the wedding photographer?",
        a: "Ideally six to twelve months ahead, popular dates in peak season are often booked early.",
      },
      {
        q: "How long does the photographer accompany us?",
        a: "Anything from a few hours to full-day coverage is possible. It depends on your schedule and budget.",
      },
      {
        q: "When do we receive the images?",
        a: "Careful selection and editing usually takes a few weeks. A small preview is often available in advance.",
      },
    ],
  },
  {
    slug: "produktfotografie",
    slugEn: "product-photography",
    theme: "product",
    label: "Produktfotografie",
    labelEn: "Product Photography",
    title: "Produktfotografie: Bilder für Marken & Shops",
    titleEn: "Product Photographers for Brands & Shops",
    metaDescription:
      "Produktfotografie und Still-Life für E-Commerce und Marken: Licht, Form und Material perfekt in Szene gesetzt. Passende Fotograf:innen über OM Selected finden.",
    metaDescriptionEn:
      "Find curated product photographers for e-commerce and brands on OM Selected: light, form and material perfectly staged for shops and campaigns.",
    eyebrow: "Magazin, Produkt",
    lede: "Im E-Commerce verkauft nicht das Produkt, sondern sein Bild.",
    intro:
      "Produktfotografie ist Präzisionsarbeit. Licht, Hintergrund und Perspektive entscheiden darüber, ob ein Produkt online überzeugt. Ob cleanes Packshot auf Weiß oder atmosphärisches Still-Life mit Charakter, gute Produktbilder schaffen Vertrauen und steigern die Conversion.",
    sections: [
      {
        heading: "Packshot oder Still-Life",
        body: "Freigestellte Packshots auf Weiß sind der Standard für Shops und Marktplätze. Stimmungsvolle Still-Life-Aufnahmen mit Props und Set-Design erzählen dagegen eine Markengeschichte für Kampagnen und Social Media.",
      },
      {
        heading: "Licht und Material",
        body: "Glas, Metall, Stoff oder Lebensmittel, jedes Material verlangt eine eigene Lichtführung. Reflexionen, Texturen und Farben müssen exakt sitzen, damit das Produkt so wirkt wie in echt, nur besser.",
      },
      {
        heading: "Konsistenz über den ganzen Shop",
        body: "Ein einheitlicher Look über alle Produktbilder hinweg wirkt professionell und stärkt die Marke. Profis arbeiten mit festen Setups, damit Bildausschnitt, Licht und Farben über hunderte Artikel konstant bleiben.",
      },
    ],
    pullQuote: "Gutes Licht ist der Unterschied zwischen einem Foto und einem Verkauf.",
    checklist: [
      "Verwendungszweck definieren (Shop, Amazon, Kampagne)",
      "Anzahl der Produkte und gewünschte Perspektiven festlegen",
      "Auf einheitlichen Look über alle Bilder achten",
      "Freigestellt auf Weiß oder Still-Life mit Set entscheiden",
    ],
    faq: [
      {
        q: "Was kostet Produktfotografie?",
        a: "Häufig wird pro Produkt oder pro Bild abgerechnet. Über das Briefing erhältst du transparente Angebote passend zum Umfang.",
      },
      {
        q: "Brauche ich freigestellte Bilder auf Weiß?",
        a: "Für die meisten Online-Shops und Marktplätze ja. Für Kampagnen lohnen sich zusätzlich stimmungsvolle Still-Life-Aufnahmen.",
      },
      {
        q: "Wie schicke ich die Produkte zur Fotograf:in?",
        a: "Das wird individuell abgestimmt, per Versand oder vor Ort im Studio. Viele Fotograf:innen bieten beides an.",
      },
    ],
    eyebrowEn: "Magazine, Product",
    ledeEn: "In e-commerce it's not the product that sells, but its image.",
    introEn:
      "Product photography is precision work. Light, background and perspective decide whether a product convinces online. Whether a clean packshot on white or an atmospheric still life with character, good product images build trust and boost conversion.",
    sectionsEn: [
      {
        heading: "Packshot or still life",
        body: "Cut-out packshots on white are the standard for shops and marketplaces. Atmospheric still-life shots with props and set design, on the other hand, tell a brand story for campaigns and social media.",
      },
      {
        heading: "Light and material",
        body: "Glass, metal, fabric or food, every material demands its own lighting. Reflections, textures and colours must be exact so the product looks like it does in real life, only better.",
      },
      {
        heading: "Consistency across the whole shop",
        body: "A uniform look across all product images feels professional and strengthens the brand. Pros work with fixed setups so framing, light and colour stay consistent across hundreds of items.",
      },
    ],
    pullQuoteEn: "Good light is the difference between a photo and a sale.",
    checklistEn: [
      "Define the purpose (shop, Amazon, campaign)",
      "Set the number of products and desired angles",
      "Aim for a consistent look across all images",
      "Decide between cut-out on white or still life with a set",
    ],
    faqEn: [
      {
        q: "What does product photography cost?",
        a: "It's often billed per product or per image. Through the briefing you'll receive transparent offers tailored to the scope.",
      },
      {
        q: "Do I need cut-out images on white?",
        a: "For most online shops and marketplaces, yes. For campaigns, atmospheric still-life shots are also worthwhile.",
      },
      {
        q: "How do I send the products to the photographer?",
        a: "This is arranged individually, by shipping or on-site in the studio. Many photographers offer both.",
      },
    ],
  },
  {
    slug: "fashionfotografie",
    slugEn: "fashion-photography",
    theme: "fashion",
    label: "Fashionfotografie",
    labelEn: "Fashion Photography",
    title: "Fashionfotografie: Mode & Kampagnen buchen",
    titleEn: "Fashion Photographers & Visagists, OM Selected",
    metaDescription:
      "Fashionfotografie mit Haltung: Looks, Lookbooks und Kampagnen, die Mode zum Erzählen bringen. Passende Fashion-Fotograf:innen über OM Selected finden.",
    metaDescriptionEn:
      "Find curated fashion photographers and visagists on OM Selected: looks, lookbooks and campaigns that make fashion tell a story.",
    eyebrow: "Magazin, Fashion",
    lede: "Mode wird erst zum Bild, wenn Stoff, Licht und Bewegung zusammenfinden.",
    intro:
      "Fashionfotografie verbindet Styling, Bewegung und Atmosphäre zu einer eigenen Bildsprache. Es geht nicht nur darum, ein Kleidungsstück zu zeigen, sondern eine Haltung, ein Lebensgefühl, eine Marke. Zwischen Studio und Location, zwischen Editorial und Kampagne entsteht der Look, der eine Kollektion begehrlich macht.",
    sections: [
      {
        heading: "Editorial, Lookbook oder Kampagne",
        body: "Ein Lookbook zeigt Kollektionen klar und konsistent, eine Kampagne erzählt die große Markengeschichte, eine redaktionelle Strecke setzt auf Stimmung und Konzept. Jedes Format verlangt eine andere Bildsprache und ein anderes Team.",
      },
      {
        heading: "Team und Styling",
        body: "Fashion ist Teamarbeit: Fotograf:in, Styling, Hair & Make-up und Modelcasting greifen ineinander. Die besten Ergebnisse entstehen, wenn alle dieselbe Vision teilen, vom Moodboard bis zum letzten Detail am Set.",
      },
      {
        heading: "Licht, Bewegung und Location",
        body: "Ob hartes Blitzlicht im Studio oder natürliches Licht on Location, die Lichtführung definiert den Charakter der Strecke. Bewegung bringt Stoffe zum Leben und macht aus statischen Looks dynamische Bilder.",
      },
    ],
    pullQuote: "Gute Modefotografie verkauft kein Kleid, sie verkauft ein Gefühl.",
    checklist: [
      "Format klären (Editorial, Lookbook, Kampagne)",
      "Moodboard und Referenzen zusammenstellen",
      "Team früh planen (Styling, Hair & Make-up, Casting)",
      "Location und Lichtstimmung gemeinsam abstimmen",
    ],
    faq: [
      {
        q: "Was kostet eine Fashion-Produktion?",
        a: "Das hängt stark von Umfang, Team und Nutzungsrechten ab. Über das Briefing auf OM Selected erhältst du passende Angebote für dein Projekt.",
      },
      {
        q: "Stellt die Fotograf:in auch das Team?",
        a: "Viele Fashion-Fotograf:innen arbeiten mit eingespielten Teams aus Styling, Hair & Make-up und Modelagenturen und stellen das komplette Set-up zusammen.",
      },
      {
        q: "Studio oder Location, was ist besser?",
        a: "Beides hat seinen Reiz. Studio gibt volle Kontrolle über Licht, Location bringt Atmosphäre und Kontext. Die Wahl richtet sich nach Konzept und Budget.",
      },
    ],
    eyebrowEn: "Magazine, Fashion",
    ledeEn: "Fashion only becomes an image when fabric, light and movement come together.",
    introEn:
      "Fashion photography combines styling, movement and atmosphere into a visual language of its own. It's not just about showing a garment, but an attitude, a way of life, a brand. Between studio and location, between editorial and campaign, the look that makes a collection desirable comes to life.",
    sectionsEn: [
      {
        heading: "Editorial, lookbook or campaign",
        body: "A lookbook shows collections clearly and consistently, a campaign tells the bigger brand story, an editorial spread relies on mood and concept. Each format demands a different visual language and a different team.",
      },
      {
        heading: "Team and styling",
        body: "Fashion is teamwork: photographer, styling, hair & make-up and model casting all interlock. The best results emerge when everyone shares the same vision, from the moodboard to the last detail on set.",
      },
      {
        heading: "Light, movement and location",
        body: "Whether hard flash in the studio or natural light on location, the lighting defines the character of the shoot. Movement brings fabrics to life and turns static looks into dynamic images.",
      },
    ],
    pullQuoteEn: "Good fashion photography doesn't sell a dress, it sells a feeling.",
    checklistEn: [
      "Clarify the format (editorial, lookbook, campaign)",
      "Put together a moodboard and references",
      "Plan the team early (styling, hair & make-up, casting)",
      "Agree on location and lighting mood together",
    ],
    faqEn: [
      {
        q: "What does a fashion production cost?",
        a: "It depends heavily on scope, team and usage rights. Through the briefing on OM Selected you'll receive matching offers for your project.",
      },
      {
        q: "Does the photographer provide the team too?",
        a: "Many fashion photographers work with established teams of styling, hair & make-up and model agencies and assemble the complete set-up.",
      },
      {
        q: "Studio or location, which is better?",
        a: "Both have their appeal. The studio gives full control over light, a location brings atmosphere and context. The choice depends on concept and budget.",
      },
    ],
  },
  {
    slug: "editorialfotografie",
    slugEn: "editorial-photography",
    theme: "editorial",
    label: "Editorialfotografie",
    labelEn: "Editorial Photography",
    title: "Editorialfotografie: Redaktionelle Strecken",
    titleEn: "Editorial Photographers & Visagists, OM Selected",
    metaDescription:
      "Editorialfotografie für Magazine und Marken: Geschichten in Bildern, mit Konzept und Haltung erzählt. Kuratierte Editorial-Fotograf:innen über OM Selected finden.",
    metaDescriptionEn:
      "Find curated editorial photographers and visagists on OM Selected: stories told in images for magazines and brands, with concept and attitude.",
    eyebrow: "Magazin, Editorial",
    lede: "Eine gute Strecke erzählt eine Geschichte, die ohne Worte verstanden wird.",
    intro:
      "Editorialfotografie ist erzählende Fotografie. Statt einzelner Hero-Bilder entsteht eine Sequenz, die ein Thema, eine Stimmung oder eine Person über mehrere Seiten trägt. Konzept, Dramaturgie und ein eigener Blick machen den Unterschied zwischen einer Bildersammlung und einer echten Strecke.",
    sections: [
      {
        heading: "Konzept vor Kamera",
        body: "Am Anfang steht eine Idee: ein Thema, eine These, eine Geschichte. Aus dem Konzept entstehen Moodboard, Locations und Bildabfolge. Erst dann kommt die Kamera ins Spiel, sie setzt um, was vorher gedacht wurde.",
      },
      {
        heading: "Dramaturgie einer Strecke",
        body: "Eine Strecke hat einen Anfang, einen Höhepunkt und ein Ende. Weite und Nähe, Ruhe und Spannung, Detail und Übersicht wechseln sich ab, damit die Bilder als Folge funktionieren und nicht nur einzeln.",
      },
      {
        heading: "Für Magazine und Marken",
        body: "Ob redaktionelle Veröffentlichung oder Brand-Editorial, der erzählerische Ansatz schafft Tiefe und Glaubwürdigkeit. Auf OM Selected findest du Fotograf:innen, die in Geschichten denken statt nur in Einzelbildern.",
      },
    ],
    pullQuote: "Editorial heißt: nicht das schönste Bild, sondern das richtige.",
    checklist: [
      "Thema und Kernbotschaft schärfen",
      "Bildabfolge und Umfang grob skizzieren",
      "Locations und Protagonist:innen früh planen",
      "Genug Raum für Konzept und Dramaturgie lassen",
    ],
    faq: [
      {
        q: "Was unterscheidet Editorial von Kampagne?",
        a: "Editorial erzählt eine Geschichte mit konzeptioneller Freiheit, eine Kampagne verfolgt ein klares Werbeziel. Beide profitieren von starkem visuellem Storytelling.",
      },
      {
        q: "Wie viele Bilder umfasst eine Strecke?",
        a: "Das variiert je nach Format, oft zwischen sechs und zwanzig Bildern, die als zusammenhängende Sequenz funktionieren.",
      },
      {
        q: "Eignet sich Editorial auch für Marken?",
        a: "Ja. Brand-Editorials verbinden redaktionelle Bildsprache mit Markenbotschaft und wirken dadurch glaubwürdiger als klassische Werbung.",
      },
    ],
    eyebrowEn: "Magazine, Editorial",
    ledeEn: "A good spread tells a story that is understood without words.",
    introEn:
      "Editorial photography is narrative photography. Instead of single hero images, a sequence emerges that carries a theme, a mood or a person across several pages. Concept, dramaturgy and a distinct eye make the difference between a collection of pictures and a real spread.",
    sectionsEn: [
      {
        heading: "Concept before camera",
        body: "It starts with an idea: a theme, a thesis, a story. From the concept come the moodboard, locations and image sequence. Only then does the camera come into play, realising what was thought out beforehand.",
      },
      {
        heading: "The dramaturgy of a spread",
        body: "A spread has a beginning, a climax and an end. Wide and close, calm and tension, detail and overview alternate so the images work as a sequence and not just individually.",
      },
      {
        heading: "For magazines and brands",
        body: "Whether an editorial publication or a brand editorial, the narrative approach creates depth and credibility. On OM Selected you'll find photographers who think in stories rather than just single images.",
      },
    ],
    pullQuoteEn: "Editorial means: not the most beautiful image, but the right one.",
    checklistEn: [
      "Sharpen the theme and core message",
      "Roughly sketch the image sequence and scope",
      "Plan locations and protagonists early",
      "Leave enough room for concept and dramaturgy",
    ],
    faqEn: [
      {
        q: "What sets editorial apart from a campaign?",
        a: "Editorial tells a story with conceptual freedom, a campaign pursues a clear advertising goal. Both benefit from strong visual storytelling.",
      },
      {
        q: "How many images does a spread contain?",
        a: "It varies by format, often between six and twenty images that work as a coherent sequence.",
      },
      {
        q: "Is editorial suitable for brands too?",
        a: "Yes. Brand editorials combine editorial visual language with a brand message and feel more credible than classic advertising.",
      },
    ],
  },
  {
    slug: "qualitaet-hat-ihren-preis",
    slugEn: "why-quality-has-its-price",
    theme: "business",
    label: "Business & Pricing",
    labelEn: "Business & Pricing",
    title: "Warum Qualität ihren Preis hat: Guide für Kunden",
    titleEn: "Why Quality Has Its Price: A Guide for Clients",
    metaDescription:
      "Was steckt wirklich im Preis eines Shootings? Vorbereitung, Ausrüstung, Make-up-Produkte und Bildbearbeitung erklärt. Ein Guide für Kunden, die Premium-Kreative buchen.",
    metaDescriptionEn:
      "What really goes into the price of a shoot? Preparation, equipment, make-up products and image editing explained. A guide for clients booking premium creatives.",
    eyebrow: "Magazin, Business",
    lede: "Ein fairer Preis ist kein Aufschlag, sondern die Summe aus Können, Zeit und Verantwortung.",
    intro:
      "Wenn du zum ersten Mal ein professionelles Shooting buchst, wirkt der Preis schnell hoch. Doch hinter einer einzigen fertigen Aufnahme steckt deutlich mehr als die Stunden vor der Kamera. Vorbereitung, hochwertige Ausrüstung, die Produkte der Visagist:innen und die stundenlange Bildbearbeitung gehören genauso dazu wie die jahrelange Erfahrung, die ein konstant gutes Ergebnis überhaupt erst möglich macht. Dieser Guide schlüsselt auf, wofür du wirklich bezahlst, damit du den Wert hinter dem Preis siehst.",
    sections: [
      {
        heading: "Die unsichtbare Arbeit vor dem Shooting",
        body: "Bevor der erste Auslöser klickt, ist oft schon ein halber Arbeitstag vergangen. Konzept, Moodboard, Location-Scouting, Abstimmung mit dem Team, Anfahrt und Aufbau gehören fest zur Leistung. Diese Vorbereitung entscheidet, ob ein Shooting reibungslos läuft und das Ergebnis sitzt.",
      },
      {
        heading: "Ausrüstung, die im Preis steckt",
        body: "Professionelle Kameras, Objektive, Licht und Modifier kosten schnell den Gegenwert eines Kleinwagens und müssen gewartet, versichert und regelmäßig ersetzt werden. Backup-Bodies und doppelte Speicherkarten sind kein Luxus, sondern Absicherung, damit deine Bilder garantiert entstehen.",
      },
      {
        heading: "Die Produkte der Visagist:innen",
        body: "Hochwertiges Make-up, Pinsel, Hygieneartikel und Pflegeprodukte sind teuer und halten nur begrenzt. Eine erfahrene Visagistin investiert laufend in ihr Kit, damit jeder Look auf der Haut und vor der Kamera perfekt sitzt. Diese Produkte sind ein realer Kostenpunkt hinter jedem Beauty-Ergebnis.",
      },
      {
        heading: "Stundenlange Bildbearbeitung",
        body: "Nach dem Shooting beginnt die eigentliche Feinarbeit. Auswahl, Farbkorrektur, Retusche und Feinschliff dauern pro Bild oft länger als die Aufnahme selbst. Was am Ende mühelos aussieht, ist das Ergebnis vieler konzentrierter Stunden am Bildschirm.",
      },
      {
        heading: "Erfahrung, Verlässlichkeit und Recht",
        body: "Du bezahlst nicht nur Zeit und Material, sondern auch Sicherheit. Profis liefern verlässlich, kennen Bildrechte und Nutzungslizenzen, arbeiten mit Verträgen und tragen das unternehmerische Risiko. Diese Verlässlichkeit ist der Unterschied zwischen einem Versuch und einem Ergebnis, auf das du dich verlassen kannst.",
      },
    ],
    pullQuote: "Du zahlst nicht für eine Stunde vor der Kamera, sondern für die Jahre, die dahinterstehen.",
    checklist: [
      "Frage nach Nutzungsrechten und Lizenzen, nicht nur nach dem Stundensatz",
      "Vergleiche Gesamtleistung statt reiner Preise",
      "Plane Vorbereitung und Bildbearbeitung als Teil der Leistung ein",
      "Achte auf Verlässlichkeit, Referenzen und einen klaren Vertrag",
      "Sieh den Preis als Investition in ein Ergebnis, das bleibt",
    ],
    faq: [
      {
        q: "Warum ist professionelle Fotografie teurer als gedacht?",
        a: "Weil der Preis weit mehr umfasst als die Zeit vor der Kamera: Vorbereitung, Ausrüstung, Produkte, Bildbearbeitung sowie Erfahrung und unternehmerisches Risiko sind alle einkalkuliert.",
      },
      {
        q: "Was rechtfertigt die Preise von Premium-Kreativen?",
        a: "Konstante Qualität, Verlässlichkeit, professionelle Ausrüstung und stundenlange Nachbearbeitung. Du zahlst für ein planbares Ergebnis statt für ein Risiko.",
      },
      {
        q: "Lohnt sich die Investition in hochwertige Kreative?",
        a: "Ja. Gute Bilder wirken über Jahre, stärken deine Marke und sparen teure Nachshootings. Günstig wird schnell teuer, wenn das Ergebnis nicht überzeugt.",
      },
    ],
    eyebrowEn: "Magazine, Business",
    ledeEn: "A fair price is not a surcharge, it is the sum of skill, time and responsibility.",
    introEn:
      "When you book a professional shoot for the first time, the price can feel high. Yet a single finished image involves far more than the hours in front of the camera. Preparation, high-end equipment, the products used by make-up artists and hours of image editing all play their part, as does the years of experience that make a consistently strong result possible in the first place. This guide breaks down what you are really paying for, so you can see the value behind the price.",
    sectionsEn: [
      {
        heading: "The invisible work before the shoot",
        body: "Before the first shutter clicks, half a working day has often already passed. Concept, moodboard, location scouting, coordinating the team, travel and setup are all part of the service. This preparation decides whether a shoot runs smoothly and the result lands.",
      },
      {
        heading: "Equipment baked into the price",
        body: "Professional cameras, lenses, lighting and modifiers quickly cost as much as a small car and must be maintained, insured and regularly replaced. Backup bodies and dual memory cards are not a luxury but insurance, so your images are guaranteed to happen.",
      },
      {
        heading: "The products make-up artists use",
        body: "High-quality make-up, brushes, hygiene items and skincare are expensive and have a limited life. An experienced make-up artist constantly reinvests in their kit so every look sits perfectly on the skin and on camera. These products are a real cost behind every beauty result.",
      },
      {
        heading: "Hours of image editing",
        body: "After the shoot, the real fine work begins. Selection, colour grading, retouching and polishing often take longer per image than the capture itself. What looks effortless in the end is the result of many focused hours at the screen.",
      },
      {
        heading: "Experience, reliability and rights",
        body: "You are not only paying for time and materials, but also for certainty. Professionals deliver reliably, understand image rights and usage licences, work with contracts and carry the business risk. That reliability is the difference between an attempt and a result you can count on.",
      },
    ],
    pullQuoteEn: "You are not paying for an hour in front of the camera, but for the years behind it.",
    checklistEn: [
      "Ask about usage rights and licences, not just the hourly rate",
      "Compare the full service rather than headline prices",
      "Account for preparation and editing as part of the work",
      "Look for reliability, references and a clear contract",
      "See the price as an investment in a result that lasts",
    ],
    faqEn: [
      {
        q: "Why is professional photography pricier than expected?",
        a: "Because the price covers far more than time in front of the camera: preparation, equipment, products, image editing as well as experience and business risk are all factored in.",
      },
      {
        q: "What justifies the prices of premium creatives?",
        a: "Consistent quality, reliability, professional equipment and hours of post-production. You pay for a predictable result rather than a gamble.",
      },
      {
        q: "Is investing in high-quality creatives worth it?",
        a: "Yes. Good images work for years, strengthen your brand and save costly reshoots. Cheap quickly becomes expensive when the result falls short.",
      },
    ],
  },
];

export function getMagazineTopic(slug: string): MagazineTopic | undefined {
  return magazineTopics.find((t) => t.slug === slug || t.slugEn === slug);
}